import { Code } from 'constants/keyboard';
import { useEventListener } from 'hooks/useEventListener';
import { memo } from 'react';
import { useEditor as useInfographEditor } from 'widgets/sdk';

import { WidgetState } from '../common/TextBasedWidgets.types';
import { TextWidgetControlKeyboardShortcutsProps } from './TextWidget.types';

// custom text widget keyboard shortcuts
export const TextWidgetControlKeyboardShortcuts = memo(
  ({ setWidgetState, widgetState, editor, dispatchUpdateWidget }: TextWidgetControlKeyboardShortcutsProps) => {
    const {
      setWidgetToolbarFocus,
      isWidgetFocused,
      isCanvasFocused,
      isElementInToolbarFocused,
      onSetNextActiveWidget,
    } = useInfographEditor();

    const onKeyDown = (e: KeyboardEvent | Event) => {
      const isToolbarFocused = isElementInToolbarFocused();

      const isEditable = widgetState === WidgetState.Edit;
      const isActive = widgetState === WidgetState.Active;
      const isDefault = widgetState === WidgetState.Default;

      const event = e as KeyboardEvent;
      const code = event.code as Code;

      const isCtrl: boolean = event.ctrlKey;
      const isMod: boolean = event.metaKey || isCtrl;
      const isAlt: boolean = event.altKey;

      switch (code) {
        case Code.Escape:
          if (isToolbarFocused) {
            return editor.chain().focus();
          } else return setWidgetState(WidgetState.Active);
        case Code.Enter:
          if (isActive || isDefault) {
            if (isWidgetFocused()) return setWidgetState(WidgetState.Edit);
            if (isCanvasFocused()) return onSetNextActiveWidget();
          }
          break;
        case Code.T:
          if (isMod && isAlt && isEditable) return setWidgetToolbarFocus();
          break;
        case Code.Tab:
          if (isEditable && !isToolbarFocused) {
            e.preventDefault();
            return;
          }
          break;

        // Text editing shortcuts for active state
        // tiptap keyboard shortcuts wont apply since editable is false => add shortcuts manually here
        // toggle bold
        case Code.B:
          if (isActive && isMod && !isAlt) {
            editor?.commands.focus('all');
            const previousHeight = editor?.view.dom.clientHeight ?? 0;
            editor.chain().focus().toggleBold().run();
            dispatchUpdateWidget(undefined, undefined, previousHeight);
          }
          break;
        // toggle italic
        case Code.I:
          if (isActive && isMod && !isAlt) {
            editor?.commands.focus('all');
            const previousHeight = editor?.view.dom.clientHeight ?? 0;
            editor.chain().focus().toggleItalic().run();
            dispatchUpdateWidget(undefined, undefined, previousHeight);
          }
          break;
        // toggle underline
        case Code.U:
          if (isActive && isMod && !isAlt) {
            editor?.commands.focus('all');
            const previousHeight = editor?.view.dom.clientHeight ?? 0;
            editor.chain().focus().toggleUnderline().run();
            dispatchUpdateWidget(undefined, undefined, previousHeight);
          }
          break;
      }
    };

    useEventListener('keydown', onKeyDown);

    return <></>;
  },
);
TextWidgetControlKeyboardShortcuts.displayName = 'TextWidgetControlKeyboardShortcuts';
